import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus, Loader2, X, Building2, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import CRMCompanyDetail from './CRMCompanyDetail';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const CRMCompanies = ({ embedded = false, navParams = {} }) => {
  const [config, setConfig] = useState({ pipeline_stages: [], account_statuses: [], lead_sources: [] });
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [stageCounts, setStageCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Preset from Command Center's "View All" links (navParams.stage/scored/responded) — one-time initial value only.
  const [stageFilter, setStageFilter] = useState(() => navParams.stage || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [scoredOnly, setScoredOnly] = useState(() => navParams.scored === 'true');
  const [respondedOnly, setRespondedOnly] = useState(() => navParams.responded === 'true');
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { fetchWithAuth(`${API_URL}/api/crm/config`).then(setConfig).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
    if (search) params.set('search', search);
    if (stageFilter) params.set('pipeline_stage', stageFilter);
    if (statusFilter) params.set('account_status', statusFilter);
    if (sourceFilter) params.set('lead_source', sourceFilter);
    if (overdueOnly) params.set('overdue_only', 'true');
    if (scoredOnly) params.set('signal_scored_only', 'true');
    if (respondedOnly) params.set('has_response', 'true');
    if (duplicatesOnly) params.set('flagged_duplicates_only', 'true');
    fetchWithAuth(`${API_URL}/api/crm/companies?${params.toString()}`)
      .then(d => { setCompanies(d.companies); setTotal(d.total); setStageCounts(d.stage_counts); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [search, stageFilter, statusFilter, sourceFilter, overdueOnly, scoredOnly, respondedOnly, duplicatesOnly, page]);

  useEffect(() => { load(); }, [load]);

  const isOverdue = (c) => c.next_action_due_date && c.next_action_due_date <= new Date().toISOString().slice(0, 10);

  return (
    <IntelligenceLayout title="Companies" embedded={embedded}>
      <div className="p-6 space-y-4" data-testid="crm-companies-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Companies</h1>
            <p className="text-sm text-gray-500 mt-1">{total.toLocaleString()} accounts tracked</p>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="new-company-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 transition-colors">
            <Plus className="h-4 w-4" /> New Company
          </button>
        </div>

        <div className="flex items-center flex-wrap gap-2" data-testid="crm-companies-filters">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <input value={search} onChange={e => { setPage(0); setSearch(e.target.value); }} placeholder="Search companies..."
              data-testid="company-search-input" className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg w-56 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          </div>
          <FilterSelect testId="stage-filter" value={stageFilter} onChange={v => { setPage(0); setStageFilter(v); }} placeholder="All Stages" options={config.pipeline_stages} />
          <FilterSelect testId="status-filter" value={statusFilter} onChange={v => { setPage(0); setStatusFilter(v); }} placeholder="All Statuses" options={config.account_statuses} />
          <FilterSelect testId="source-filter" value={sourceFilter} onChange={v => { setPage(0); setSourceFilter(v); }} placeholder="All Sources" options={config.lead_sources} />
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-2 cursor-pointer" data-testid="overdue-filter-toggle">
            <input type="checkbox" checked={overdueOnly} onChange={e => { setPage(0); setOverdueOnly(e.target.checked); }} className="rounded" />
            Overdue only
          </label>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-2 cursor-pointer" data-testid="scored-filter-toggle">
            <input type="checkbox" checked={scoredOnly} onChange={e => { setPage(0); setScoredOnly(e.target.checked); }} className="rounded" />
            High-Priority Prospects
          </label>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-2 cursor-pointer" data-testid="responded-filter-toggle">
            <input type="checkbox" checked={respondedOnly} onChange={e => { setPage(0); setRespondedOnly(e.target.checked); }} className="rounded" />
            Has Response
          </label>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-2 cursor-pointer" data-testid="duplicates-filter-toggle">
            <input type="checkbox" checked={duplicatesOnly} onChange={e => { setPage(0); setDuplicatesOnly(e.target.checked); }} className="rounded" />
            Possible Duplicates
          </label>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : companies.length === 0 ? (
            <p className="text-sm text-gray-400 p-8 text-center">No companies match these filters.</p>
          ) : (
            <table className="w-full text-sm" data-testid="companies-table">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2.5 px-4">Company</th>
                  <th className="text-left py-2.5 px-4">Industry / Location</th>
                  <th className="text-left py-2.5 px-4">Stage</th>
                  <th className="text-left py-2.5 px-4">Status</th>
                  <th className="text-left py-2.5 px-4">Next Action</th>
                  <th className="text-left py-2.5 px-4">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {companies.map(c => (
                  <tr key={c.id} onClick={() => setSelectedId(c.id)} data-testid={`company-row-${c.id}`}
                    className="border-t border-gray-100 hover:bg-[#fdf9ef] cursor-pointer transition-colors">
                    <td className="py-2.5 px-4 font-medium text-[#0A1F44]">
                      <div className="flex items-center gap-1.5">
                        {c.name}
                        {c.potential_duplicate_domain && (
                          c.duplicate_review_status === 'confirmed_not_duplicate' ? (
                            <span title={c.duplicate_review_note || 'Manually reviewed — confirmed not a duplicate'}
                              data-testid={`duplicate-reviewed-${c.id}`} className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 border border-gray-200 rounded font-medium">
                              Reviewed — kept separate
                            </span>
                          ) : (
                            <span title={`Shares domain with: ${(c.potential_duplicate_matches || []).map(m => m.name).join(', ')}`}
                              data-testid={`duplicate-flag-${c.id}`} className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 border border-red-200 rounded font-medium">
                              <AlertTriangle className="h-2.5 w-2.5" /> Possible duplicate
                            </span>
                          )
                        )}
                      </div>
                      {(scoredOnly || respondedOnly) && (
                        <p className="text-[11px] text-gray-400 font-normal mt-0.5">
                          {scoredOnly && `Score ${c.signal_score ?? '—'} (${c.signal_priority || 'unranked'})`}
                          {scoredOnly && respondedOnly && ' · '}
                          {respondedOnly && `Responded ${c.last_response_at ? c.last_response_at.slice(0, 10) : '—'} (${c.last_response_status || '—'})`}
                        </p>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-gray-500">{[c.industry, c.location].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="py-2.5 px-4"><StageBadge stage={c.pipeline_stage} /></td>
                    <td className="py-2.5 px-4 text-gray-600">{c.account_status || '—'}</td>
                    <td className={`py-2.5 px-4 ${isOverdue(c) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      {c.next_action || '—'}{c.next_action_due_date ? ` (${c.next_action_due_date})` : ''}
                    </td>
                    <td className="py-2.5 px-4 text-gray-400">{c.last_activity_at ? c.last_activity_at.slice(0, 10) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}</span>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="companies-prev-page" className="px-3 py-1 border border-gray-200 rounded-lg disabled:opacity-40">Prev</button>
              <button disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)} data-testid="companies-next-page" className="px-3 py-1 border border-gray-200 rounded-lg disabled:opacity-40">Next</button>
            </div>
          </div>
        )}

        {Object.keys(stageCounts).length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs text-gray-500" data-testid="stage-counts-summary">
            {Object.entries(stageCounts).filter(([, n]) => n > 0).map(([s, n]) => <span key={s} className="px-2 py-1 bg-gray-50 rounded border border-gray-200">{s}: <b className="text-[#0A1F44]">{n}</b></span>)}
          </div>
        )}
      </div>

      {selectedId && (
        <CRMCompanyDetail companyId={selectedId} config={config} onClose={() => setSelectedId(null)} onUpdated={load} />
      )}
      {showCreate && (
        <CreateCompanyModal config={config} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </IntelligenceLayout>
  );
};

export const STAGE_COLORS = {
  'Prospect': 'bg-gray-100 text-gray-700', 'Outreach': 'bg-blue-100 text-blue-700', 'Response': 'bg-indigo-100 text-indigo-700',
  'Discovery': 'bg-purple-100 text-purple-700', 'Agreement Sent': 'bg-amber-100 text-amber-700', 'Agreement Signed': 'bg-orange-100 text-orange-700',
  'Active Client': 'bg-emerald-100 text-emerald-700', 'Job Order': 'bg-teal-100 text-teal-700', 'Placement / Won': 'bg-green-100 text-green-700',
  'Lost / Closed': 'bg-red-100 text-red-700',
};

export const StageBadge = ({ stage }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STAGE_COLORS[stage] || 'bg-gray-100 text-gray-700'}`}>{stage || 'Prospect'}</span>
);

const FilterSelect = ({ testId, value, onChange, placeholder, options }) => (
  <select value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
    className="text-xs font-medium px-3 py-2 border border-gray-200 rounded-lg text-gray-600 focus:outline-none focus:ring-1 focus:ring-[#D4AF37] bg-white">
    <option value="">{placeholder}</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const CreateCompanyModal = ({ config, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', website: '', industry: '', location: '', account_owner: '', lead_source: 'Manual' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/companies`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      toast.success('Company created');
      onCreated();
    } catch (e) {
      toast.error('Could not create company — it may already exist');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="create-company-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-[#0A1F44] flex items-center gap-2"><Building2 className="h-4 w-4" /> New Company</h3>
          <button onClick={onClose} data-testid="close-create-company-modal"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        {['name', 'website', 'industry', 'location', 'account_owner'].map(field => (
          <input key={field} value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })}
            placeholder={field === 'name' ? 'Company name *' : field.replace('_', ' ')} data-testid={`create-company-${field}`}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]" />
        ))}
        <button onClick={submit} disabled={saving} data-testid="create-company-submit"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Creating...' : 'Create Company'}
        </button>
      </div>
    </div>
  );
};

export default CRMCompanies;
